import { createRecord } from '../recordStorageService';
import { CREDIT_BLOCK_SYSTEM_CASE_ORIGIN } from './caseOriginDisplay';
import { logRecordCreated } from '../activityLogService';
import { persistedTaskRecords } from '../mockData/tasks';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { ListViewRecord } from '../../types/listView';
import { applyCreditBlockTaskRelationFields } from './creditBlockTaskRelations';

export type CreditBlockIntakeKind = 'credit_pull' | 'lock_request';

export interface CreditBlockIntakeActor {
  userId: string;
  userName: string;
  role: string;
}

export interface CreditBlockIntakeResult {
  caseId: string;
  taskId: string;
}

const dueDateStr = (daysFromNow: number) => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split('T')[0];
};

function intakeLabels(kind: CreditBlockIntakeKind) {
  if (kind === 'credit_pull') {
    return {
      caseType: 'Credit Pull',
      taskType: 'Credit Pull',
      caseSubjectPrefix: 'Credit pull request',
      taskSubjectPrefix: 'Complete credit pull',
    };
  }
  return {
    caseType: 'Lock Request',
    taskType: 'Lock / Block',
    caseSubjectPrefix: 'Lock request',
    taskSubjectPrefix: 'Process lock / block request',
  };
}

/**
 * Creates a Case + follow-up Task from Credit Block Operations intake.
 * Logs activity for assignee + manager visibility (metadata.audience).
 * When not on Gobodo, also appends the task to persistedTaskRecords so Home/CB dashboards pick it up.
 */
export async function runCreditBlockOpsIntake(
  kind: CreditBlockIntakeKind,
  actor: CreditBlockIntakeActor,
  assigneeName: string
): Promise<CreditBlockIntakeResult> {
  const labels = intakeLabels(kind);
  const stamp = new Date().toISOString();
  const shortStamp = stamp.slice(0, 16).replace('T', ' ');

  const caseSubject = `${labels.caseSubjectPrefix} — ${shortStamp}`;
  const casePayload: Record<string, unknown> = {
    Subject__c: caseSubject,
    subject: caseSubject,
    Status__c: 'New',
    status: 'New',
    Priority__c: 'Medium',
    priority: 'Medium',
    Type__c: labels.caseType,
    type: labels.caseType,
    Origin__c: CREDIT_BLOCK_SYSTEM_CASE_ORIGIN,
    origin: CREDIT_BLOCK_SYSTEM_CASE_ORIGIN,
    Description__c: `Intake initiated by ${actor.userName} (${actor.role}). Assigned processor: ${assigneeName}.`,
    Owner__c: assigneeName,
    owner: assigneeName,
  };

  const caseRec = await createRecord('Case__c', casePayload as Record<string, unknown>);
  const caseId = String(caseRec.id);

  const taskSubject = `${labels.taskSubjectPrefix} (${shortStamp})`;
  const taskPayload: Record<string, unknown> = {
    Subject__c: taskSubject,
    title: taskSubject,
    Description__c: `Case ${caseId}. ${labels.taskSubjectPrefix}. Initiated by ${actor.userName}.`,
    Status__c: 'Open',
    status: 'Open',
    Priority__c: 'High',
    priority: 'High',
    Type__c: labels.taskType,
    type: labels.taskType,
    Owner__c: assigneeName,
    Assignee__c: assigneeName,
    assignee: assigneeName,
    DueDate__c: dueDateStr(3),
    dueDate: dueDateStr(3),
    RelatedTo__c: caseId,
    RelatedObjectType__c: 'Case__c',
    Source__c: 'credit_block_ops_intake',
    source: 'Credit Block Operations',
  };

  applyCreditBlockTaskRelationFields(taskPayload, caseId);

  const taskRec = await createRecord('Task__c', taskPayload as Record<string, unknown>);
  const taskId = String(taskRec.id);

  const auditMeta = {
    creditBlockIntake: true,
    intakeKind: kind,
    initiatedByUserId: actor.userId,
    initiatedByUserName: actor.userName,
    initiatorRole: actor.role,
    assigneeName,
    audience: 'assignee_and_manager' as const,
    parentCaseId: caseId,
    taskId,
  };

  logRecordCreated(caseId, 'Case__c', actor.userId, actor.userName, auditMeta);
  logRecordCreated(taskId, 'Task__c', actor.userId, actor.userName, {
    ...auditMeta,
    relatedCaseId: caseId,
  });

  if (!USE_GOBODO_API) {
    const row: ListViewRecord = {
      id: taskId,
      title: taskSubject,
      description: String(taskPayload.Description__c ?? ''),
      type: labels.taskType,
      status: 'Open',
      priority: 'High',
      assignee: assigneeName,
      owner: assigneeName,
      assignedDate: stamp,
      dueDate: dueDateStr(3),
      source: 'Credit Block Operations',
      clientId: '',
      clientName: '',
      createdBy: actor.userName,
      createdDate: stamp,
      lastModifiedDate: stamp,
      Subject__c: taskSubject,
      Status__c: 'Open',
      Priority__c: 'High',
      Type__c: labels.taskType,
      Assignee__c: assigneeName,
      Owner__c: assigneeName,
      DueDate__c: dueDateStr(3),
      RelatedTo__c: caseId,
    };
    persistedTaskRecords.add(row);
  }

  return { caseId, taskId };
}

/** One work task linked to a case created from the header “New Credit Block case” flow (bureau picks). */
export async function createFollowUpTaskForCreditBlockCase(params: {
  caseId: string;
  assigneeName: string;
  taskSubject: string;
  description: string;
  priority: string;
  actor: CreditBlockIntakeActor;
  parentTaskId?: string;
  clientId?: string;
  clientName?: string;
}): Promise<string> {
  const { caseId, assigneeName, taskSubject, description, priority, actor, parentTaskId, clientId, clientName } = params;
  const stamp = new Date().toISOString();
  const p = ['High', 'Medium', 'Low'].includes(priority) ? priority : 'Medium';

  const taskPayload: Record<string, unknown> = {
    Subject__c: taskSubject,
    title: taskSubject,
    Description__c: description,
    Status__c: 'Open',
    status: 'Open',
    Priority__c: p,
    priority: p,
    Type__c: 'Credit Block',
    type: 'Credit Block',
    Owner__c: assigneeName,
    Assignee__c: assigneeName,
    assignee: assigneeName,
    DueDate__c: dueDateStr(3),
    dueDate: dueDateStr(3),
    RelatedTo__c: caseId,
    RelatedObjectType__c: 'Case__c',
    Source__c: 'credit_block_new_case',
    source: 'Credit Block New Case',
  };

  if (clientId?.trim()) {
    taskPayload.ClientId__c = clientId.trim();
    taskPayload.clientId = clientId.trim();
    taskPayload.AccountId__c = clientId.trim();
  }
  if (clientName?.trim()) {
    taskPayload.ClientName__c = clientName.trim();
    taskPayload.clientName = clientName.trim();
    taskPayload.AccountName__c = clientName.trim();
  }

  applyCreditBlockTaskRelationFields(taskPayload, caseId, parentTaskId);

  const taskRec = await createRecord('Task__c', taskPayload as Record<string, unknown>);
  const taskId = String(taskRec.id);

  logRecordCreated(taskId, 'Task__c', actor.userId, actor.userName, {
    creditBlockNewCaseTask: true,
    parentCaseId: caseId,
    audience: 'assignee_and_manager' as const,
  });

  if (!USE_GOBODO_API) {
    const row: ListViewRecord = {
      id: taskId,
      title: taskSubject,
      description: String(taskPayload.Description__c ?? ''),
      type: 'Credit Block',
      status: 'Open',
      priority: p,
      assignee: assigneeName,
      owner: assigneeName,
      assignedDate: stamp,
      dueDate: dueDateStr(3),
      source: 'Credit Block New Case',
      clientId: clientId?.trim() || '',
      clientName: clientName?.trim() || '',
      createdBy: actor.userName,
      createdDate: stamp,
      lastModifiedDate: stamp,
      Subject__c: taskSubject,
      Status__c: 'Open',
      Priority__c: p,
      Type__c: 'Credit Block',
      Assignee__c: assigneeName,
      Owner__c: assigneeName,
      DueDate__c: dueDateStr(3),
      RelatedTo__c: caseId,
    };
    persistedTaskRecords.add(row);
  }

  return taskId;
}
